import React, { useState } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { 
  LayoutDashboard, 
  MapPin, 
  Calendar, 
  MessageSquare, 
  Image, 
  Award, 
  Video, 
  Tag, 
  FileText,
  LogOut,
  Menu,
  X,
  Clock,
  Building2,
  Megaphone,
  UserCheck,
  MessageCircle,
  Settings
} from 'lucide-react';

const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const allMenuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['superadmin', 'admin'] },
    { path: '/admin/media', icon: Video, label: 'Media', roles: ['superadmin', 'admin'] },
    { path: '/admin/locations', icon: MapPin, label: 'Locations', roles: ['superadmin', 'admin'] },
    { path: '/admin/bookings', icon: Calendar, label: 'Bookings', roles: ['superadmin', 'admin', 'reception'] },
    { path: '/admin/site-management', icon: Settings, label: 'Site Management', roles: ['superadmin', 'admin'] },
    { path: '/admin/agencies', icon: Building2, label: 'Agencies', roles: ['superadmin', 'admin'] },
    { path: '/admin/reception', icon: UserCheck, label: 'Reception', roles: ['superadmin', 'admin'] },
    { path: '/admin/testimonials', icon: MessageSquare, label: 'Testimonials', roles: ['superadmin', 'admin'] },
    { path: '/admin/coupons', icon: Tag, label: 'Coupons', roles: ['superadmin', 'admin'] },
    { path: '/admin/blogs', icon: FileText, label: 'Blogs', roles: ['superadmin', 'admin'] },
    { path: '/admin/bungyman', icon: MessageCircle, label: 'BungyMan', roles: ['superadmin', 'admin'] },
    { path: '/admin/contacts', icon: MessageSquare, label: 'Contacts', roles: ['superadmin', 'admin'] },
  ];

  // Filter menu items based on user role
  const userRole = admin?.role || 'superadmin';
  const menuItems = allMenuItems.filter(item => item.roles.includes(userRole));

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-800 border-r border-gray-700 transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-gray-700">
            <Link to="/admin/dashboard" className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Indya Bungy" 
                className="h-12 w-auto"
              />
              <div>
                <div className="text-white font-bold text-sm">Admin Panel</div>
                <div className="text-gray-400 text-xs">{admin?.name}</div>
              </div>
            </Link>
          </div>

          {/* Menu */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-yellow-400 text-black'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-700">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <LogOut size={20} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-gray-300 hover:text-white"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div className="text-white font-bold text-xl hidden lg:block">
              {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-white font-medium text-sm">{admin?.name}</div>
                <div className="text-gray-400 text-xs capitalize">{admin?.role}</div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;